import { useWorkplaceRequestMutations } from "@/hooks/useWorkplaceRequestMutations";
import { useWorkplaceRequestTypes } from "@/hooks/useWorkplaceRequestTypes";
import { useWorkplaceRequestsByReservation } from "@/hooks/useWorkplaceRequestsByReservation";
import {
  WorkplaceRequestsFormValues,
  workplaceRequestsFormSchema
} from "@/lib/validation/workplace-requests";
import { calculateTotalBuffer } from "@/lib/workplaceRequestBuffer";
import { WorkplaceRequestParams } from "@/types/workplace-requests";
import { Card } from "@gettactic/components/src/@v2/components/display/Card/Card";
import { Button } from "@gettactic/components/src/@v2/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@gettactic/components/src/@v2/components/ui/tooltip";
import { HookFormTextInput } from "@gettactic/components/src/form/TextInput";
import { HookFormTextareaInput } from "@gettactic/components/src/form/TextareaInput";
import { cn } from "@gettactic/helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReservationTypeSelect } from "@webV2/components/workplace-request/type-select";
import { AnimatePresence, motion } from "framer-motion";
import { Undo, X } from "lucide-react";
import React, {
  useState,
  useCallback,
  useImperativeHandle,
  useEffect
} from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { WPRequestErrors } from "./WPRequestErrors";
import { WPRequestsProps } from "./types";
import { mapWorkplaceRequestToReserveFormData } from "@gettactic/helpers/src/reserve/mapWorkplaceRequestToReserveFormData";

type RequestsFormValues = WorkplaceRequestsFormValues;

export const WPRequests: React.FC<WPRequestsProps> = ({
  reservationId,
  officeId,
  className,
  onSubmitRef,
  customTypeSelector,
  workplaceRequestItems,
  onBufferChange
}) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [nextId, setNextId] = useState(1);

  const { allElements: types } = useWorkplaceRequestTypes();
  const {
    updateMultipleWorkplaceRequests,
    deleteWorkplaceRequest,
    createMultipleWorkplaceRequests
  } = useWorkplaceRequestMutations();

  const form = useForm<RequestsFormValues>({
    resolver: zodResolver(workplaceRequestsFormSchema),
    defaultValues: {
      requests: workplaceRequestItems || []
    }
  });

  const { fields, append, remove, update } = useFieldArray({
    control: form.control,
    name: "requests"
  });

  // Watch the form values in real-time
  const watchedRequests = useWatch({
    control: form.control,
    name: "requests"
  });

  // Notify parent when buffer settings change
  useEffect(() => {
    if (onBufferChange) {
      const requests = watchedRequests || [];
      const typeIds = requests
        .filter((req) => !req.isDeleted)
        .map((req) => req.type.id);
      const buffer = calculateTotalBuffer(typeIds);
      onBufferChange(buffer);
    }
  }, [watchedRequests, onBufferChange]);

  const { allElements: existingRequests, isFetching: isLoadingExisting } =
    useWorkplaceRequestsByReservation(reservationId, {
      enabled: !!reservationId
    });

  useEffect(() => {
    if (existingRequests.length > 0 && fields.length === 0) {
      const mappedRequests = mapWorkplaceRequestToReserveFormData(
        existingRequests,
        types
      );

      // Update form with existing requests
      form.setValue("requests", mappedRequests as never);
    }
  }, [existingRequests, types, fields.length, form]);

  const handleTypeSelect = useCallback(
    (typeId: string) => {
      const selectedType = types.find((type) => type.id === typeId);
      if (!selectedType) return;

      // Add new request to the form
      const newRequest = {
        id: `new-${nextId}`,
        type: selectedType,
        title: "",
        description: "",
        in_use: false,
        isNew: true,
        isDeleted: false
      };

      // Add new request at the beginning (index 0) instead of at the end
      const currentRequests = form.getValues("requests");
      form.setValue("requests", [newRequest, ...currentRequests]);
      setNextId((prev) => prev + 1);
    },
    [types, nextId, form]
  );

  const handleRemoveRequest = useCallback(
    (index: number) => {
      // Get the current form values to ensure we have the latest state
      const currentRequests = form.getValues("requests");
      const request = currentRequests[index];

      // If it's an existing request (not new), mark it for deletion instead of removing
      if (request && !request.isNew && request.id) {
        // Mark as deleted instead of removing from form
        update(index, { ...request, isDeleted: true });
      } else {
        // For new requests, just remove from form
        remove(index);
      }
    },
    [remove, update, form]
  );

  const validateAndGetRequests = useCallback(async () => {
    const isValid = await form.trigger();
    if (!isValid) {
      return null;
    }

    return fields;
  }, [form, fields]);

  const handleResetRequest = useCallback(
    async (index: number) => {
      const request = fields[index];
      if (!request?.originalData) return;

      update(index, {
        ...request,
        title: request.originalData?.title || "",
        description: request.originalData?.description || ""
      });
    },
    [fields, update]
  );

  const submitRequests = useCallback(
    async (
      params: WorkplaceRequestParams
    ): Promise<{
      success: boolean;
      error?: string;
    }> => {
      const { scheduleId, ownerUserId } = params;
      try {
        setErrors([]);

        const requests =
          form
            .getValues("requests")
            .map((req) => {
              if (req.description && !req.title) {
                req.title = `${req.description.slice(0, 50)}...`;
              }
              return req;
            })
            .filter((req) => req.title || req.description) || [];

        if (!requests || requests.length === 0) {
          return { success: true };
        }

        // Validate and categorize requests in a single pass
        const { newRequests, existingRequests, hasInvalidRequests } =
          requests.reduce(
            (acc, req) => {
              // Check for invalid requests
              if (!req.title || !req.title.trim()) {
                acc.hasInvalidRequests = true;
              }

              // Categorize valid requests
              if (req.isNew) {
                acc.newRequests.push(req);
              } else if (!req.isDeleted && req.id) {
                acc.existingRequests.push(req);
              }
              return acc;
            },
            {
              newRequests: [] as typeof requests,
              existingRequests: [] as typeof requests,
              hasInvalidRequests: false
            }
          );

        if (hasInvalidRequests) {
          const errorMessage = "Please fill in all required fields";
          setErrors((prev) => [...prev, errorMessage]);
          return { success: false, error: errorMessage };
        }

        let hasError = false;
        let errorMessage = "";

        if (newRequests.length > 0) {
          if (!scheduleId) {
            hasError = true;
            errorMessage = "No schedule ID provided for workplace requests";
            setErrors((prev) => [...prev, errorMessage]);
          } else {
            try {
              const createData = newRequests.map((req) => {
                const requestData: any = {
                  title: req.title,
                  type_id: req.type.id,
                  office_id: officeId,
                  schedule_id: scheduleId,
                  status: "open" as const
                };

                if (req.description?.trim()) {
                  requestData.description = req.description.trim();
                }
                if (ownerUserId) {
                  requestData.submitter_id = ownerUserId;
                }
                return requestData;
              });

              await createMultipleWorkplaceRequests(createData);

              form.setValue("requests", []);

              setNextId(1);
            } catch (error) {
              hasError = true;
              errorMessage = "Failed to create new workplace requests";
              setErrors((prev) => [...prev, errorMessage]);
            }
          }
        }

        if (existingRequests.length > 0) {
          const updateData = existingRequests.map((req) => {
            const updates: any = {
              title: req.title,
              type_id: req.type.id
            };

            // Only add description if it has content
            if (req.description?.trim()) {
              updates.description = req.description.trim();
            }

            return {
              requestId: req.id!,
              updates
            };
          });

          try {
            await updateMultipleWorkplaceRequests(updateData);

            const updatedRequests = form.getValues("requests");
          } catch (error) {
            hasError = true;
            errorMessage = "Failed to update existing requests";
            setErrors((prev) => [...prev, errorMessage]);
          }
        }

        const requestsToDelete = requests.filter(
          (req) => req.isDeleted && !req.isNew && req.id
        );
        if (requestsToDelete.length > 0) {
          try {
            await Promise.all(
              requestsToDelete.map((request) =>
                deleteWorkplaceRequest(request.id)
              )
            );

            const updatedRequests = requests.filter((req) => !req.isDeleted);
            form.setValue("requests", updatedRequests);
          } catch (error) {
            hasError = true;
            errorMessage = "Failed to delete some workplace requests";
            setErrors((prev) => [...prev, errorMessage]);
          }
        }

        if (hasError) {
          return { success: false, error: errorMessage };
        }

        return { success: true };
      } catch (error) {
        const errorMessage = "Failed to process workplace requests";
        setErrors((prev) => [...prev, errorMessage]);
        return { success: false, error: errorMessage };
      }
    },
    [
      form,
      officeId,
      createMultipleWorkplaceRequests,
      updateMultipleWorkplaceRequests,
      deleteWorkplaceRequest
    ]
  );

  const getBufferSettingsFromRequests = useCallback(() => {
    const requests = form.getValues("requests") || [];
    const typeIds = requests
      .filter((req) => !req.isDeleted)
      .map((req) => req.type.id);
    return calculateTotalBuffer(typeIds);
  }, [form]);

  useImperativeHandle(
    onSubmitRef,
    () => ({
      submitWorkplaceRequests: submitRequests,
      validateAndGetRequests,
      getBufferSettings: getBufferSettingsFromRequests
    }),
    [submitRequests, validateAndGetRequests, getBufferSettingsFromRequests]
  );

  if (isLoadingExisting) {
    return (
      <div className={cn("space-y-4", className)}>
        <div className="text-gray-500 text-sm">
          Loading existing requests...
        </div>
      </div>
    );
  }

  const TypeSelectorComponent = customTypeSelector || ReservationTypeSelect;

  return (
    <div className={cn("min-h-10 space-y-4", className)}>
      <div className="flex items-start gap-3">
        <div
          className={cn(
            fields.length > 0 ? "min-h-auto" : "min-h-[60px]",
            "flex-1 min-w-0"
          )}
        >
          <TypeSelectorComponent
            onTypeSelect={handleTypeSelect}
            selectedTypes={fields.map((req) => req.type.id)}
          />
        </div>
      </div>

      <WPRequestErrors errors={errors} />

      {fields.length > 0 ? (
        <form className="space-y-3" onSubmit={(e) => e.preventDefault()}>
          <AnimatePresence mode="popLayout">
            {fields.map((field, index) => (
              <motion.div
                key={field.id}
                layoutId={field.id}
                initial={
                  field.isNew ? { opacity: 0, y: -30, scale: 0.95 } : false
                }
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -30, scale: 0.95 }}
                transition={{
                  duration: 0.3,
                  ease: "easeOut"
                }}
                layout
              >
                <Card className="relative border border-gray-200 bg-white shadow-sm">
                  <div className="">
                    {/* header start */}
                    <div
                      className={`mb-4 flex items-start justify-between ${reservationId && field.id && !field.isNew && field.isDeleted ? "opacity-50" : ""}`}
                    >
                      <div className="flex items-center gap-2">
                        <div>
                          <h4 className="text-gray-900 text-sm">
                            Request type:{" "}
                            <span className="font-bold">{field.type.name}</span>
                          </h4>
                        </div>
                        {reservationId &&
                          field.id &&
                          !field.isNew &&
                          field.isDeleted && (
                            <div className="text-gray-500 text-sm">
                              (will be removed)
                            </div>
                          )}
                        {reservationId &&
                          field.id &&
                          !field.isNew &&
                          !field.isDeleted && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleResetRequest(index)}
                                  className="relative z-30 h-6 w-6 bg-white p-0"
                                  type="button"
                                >
                                  <Undo className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Reset to previous saved state</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                      </div>

                      {reservationId &&
                      field.id &&
                      !field.isNew &&
                      field.isDeleted ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                update(index, { ...field, isDeleted: false });
                              }}
                              className="h-6 w-6 p-0 "
                            >
                              <Undo className="h-3 w-3" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Undo deletion</p>
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemoveRequest(index)}
                          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>

                    <div
                      className={`space-y-4 ${reservationId && field.id && !field.isNew && field.isDeleted ? "opacity-50" : ""}`}
                    >
                      <HookFormTextInput
                        form={form}
                        name={`requests.${index}.title`}
                        label=""
                        placeholder="Enter request title"
                        isRequired
                        className="focus-ring-rainbow h-[42px] max-h-[42px] px-0 py-0 shadow-none"
                        wrapperClassName="px-0 py-0 shadow-none"
                        inputClassName="shadow-none h-[40px]"
                        disabled={
                          !!(
                            reservationId &&
                            field.id &&
                            !field.isNew &&
                            field.isDeleted
                          )
                        }
                      />

                      <div className="focus-ring-rainbow rounded-md">
                        <HookFormTextareaInput
                          form={form}
                          name={`requests.${index}.description`}
                          placeholder="Describe the request in detail"
                          className="max-h-[100px] min-h-[60px] border-input"
                          inputClassName="bg-bg"
                          disabled={
                            !!(
                              reservationId &&
                              field.id &&
                              !field.isNew &&
                              field.isDeleted
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </form>
      ) : null}
    </div>
  );
};
